import { map, isEmpty, chain, get, every, size, some, toLower } from "lodash"

export const classificationsQuery = `query {
  classifications {
    name
    id
    proprietary
    versions{
      id
      asOf
      tiers{
        id
        level
        name
      }
    }
  }
}`

function generateClassificationValues(currentLevel, depth) {
  return ` values {
    id
    name
    ${currentLevel !== depth ? generateClassificationValues(currentLevel + 1, depth) : ""}
  }`
}

export function generateClassificationQuery(classificationsTiers) {
  const fragments = map(classificationsTiers, t => `${t.id}: classification(id: "${t.id}") {
                version(id: "${t.versionId}" ){
                  tiers { id name }
                  ${generateClassificationValues(1, t.levels)}
                }
              }`,
  )
  return fragments.join(" ")
}

export function generateClassificationFacetQuery(classifications) {
  const fragments = map(classifications, c => `
    classification_${c.id}:classification(id:"${c.id}"){
      id
      count
    }
    `)
  return fragments.join(" ")
}

export function generateClassificationCompositionQuery(classifications) {
  if (isEmpty(classifications)) { return "" }
  const fragments = chain(classifications)
    .map(c => ({
      id: c.id,
      tiers: chain(c).get("versions").first().get("tiers")
        .value(),
    }))
    .map(c => map(c.tiers, t => `
    ${c.id}_${t.id}:compositionBy(groupById:"${c.id}", classificationTier:"${t.id}", groupBy:CLASSIFICATION){
      id
      description
      economicExposure
    }
    `))
    .value()
  return fragments.join(" ")
}

export function classificationsMap(classifications, date) {
  return map(classifications, c => {
    const currentVersion = chain(c)
      .get("versions")
      .orderBy("asOf", "desc")
      .find(v => v.asOf <= date)
      .value()
    return {
      id: c.id,
      versionId: get(currentVersion, "id"),
      levels: chain(currentVersion).get("tiers").size(c.tiers).value(),
      tiers: chain(currentVersion).get("tiers").value(),
    }
  })
}

// Check all classifications to verify that they have more than one possible facet
// otherwise they are not being displayed
export function emptyOrOnlyOneFacetPerClassification(classifications) {
  return every(classifications, (c) => onlyOneFacetPerTier(c))
}

// For a specific classification, check if all its tiers only have one possible facet
export function onlyOneFacetPerTier(facets) {
  return every(facets, (f) => size(get(f, "facets")) <= 1)
}

export function getTierName(classifications, cId, tierId) {
  return chain(classifications).get(cId).find(({ tier }) => tier.id === tierId).get("tier.name")
}

export const checkClassificationAccess = (classificationId, classifications) =>
  some(classifications, c => get(c, "id") === classificationId)

export const generateClassificationQueryForContributors = (classificationId, classifications) =>
  checkClassificationAccess(classificationId, classifications)
    ? 'classification(id:"GICS" tier:"INDUSTRY"){name id}'
    : ""

export const generateGICSClassificationQueryForCompositionContributors = (classifications, tiers = ["INDUSTRY", "SECTOR"]) => {
  if (!checkClassificationAccess("GICS", classifications)) {
    return ""
  }
  return tiers.map(tier => `${toLower(tier)}: classification(id:"GICS" tier:"${tier}"){name id}`).join("\n")
}

export const generateClassificationQueryForContributorDescriptors = (id, classifications, tiers = []) => {
  if (!checkClassificationAccess(id, classifications)) {
    return ""
  }
  return tiers.map(tier => `${toLower(id)}_${toLower(tier)}: classification(id:"${id}" tier:"${tier}"){name id}`).join("\n")
}

export const getClassifications = state => get(state, "global.data.classifications.data.classifications")

export const computeClassifications = (classifications, selectedDate) => map(classifications, c => ({
  ...c,
  version: chain(c)
    .get("versions")
    .orderBy("asOf", "desc")
    .find(v => v.asOf <= selectedDate)
    .value(),
}),
)
